import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import PrivacyConsentService, { ConsentData, PrivacySettings } from '../services/PrivacyConsentService';
import CookieManager from '../utils/cookieManager';
import ConsentBanner from '../components/Privacy/ConsentBanner';

interface PrivacyContextType {
  consent: ConsentData | null;
  privacySettings: PrivacySettings | null;
  needsConsent: boolean;
  canShowAds: boolean;
  canShowPersonalizedAds: boolean;
  canTrackAnalytics: boolean;
  canUseCookies: boolean;
  updateConsent: (consent: ConsentData) => void;
  updatePrivacySettings: (settings: PrivacySettings) => void;
  clearAllData: () => void;
  refreshConsent: () => void;
}

const PrivacyContext = createContext<PrivacyContextType | undefined>(undefined);

interface PrivacyProviderProps {
  children: ReactNode;
}

export const PrivacyProvider: React.FC<PrivacyProviderProps> = ({ children }) => {
  const [consent, setConsent] = useState<ConsentData | null>(null);
  const [privacySettings, setPrivacySettings] = useState<PrivacySettings | null>(null);
  const [needsConsent, setNeedsConsent] = useState(false);
  const [showConsentBanner, setShowConsentBanner] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  const privacyService = PrivacyConsentService;
  const cookieManager = CookieManager.getInstance();

  useEffect(() => {
    initializePrivacyState();
  }, []);

  const initializePrivacyState = async () => {
    try {
      // Skip delay in test environment
      if (process.env.NODE_ENV !== 'test') {
        // Wait a bit for location detection
        await new Promise(resolve => setTimeout(resolve, 1000));
      }

      const currentConsent = privacyService.getConsent();
      const currentSettings = privacyService.getPrivacySettings();
      const needsConsentCheck = privacyService.needsConsent();

      setConsent(currentConsent);
      setPrivacySettings(currentSettings);
      setNeedsConsent(needsConsentCheck);
      setShowConsentBanner(needsConsentCheck);

      // Clean up non-consented storage
      if (currentConsent) {
        cookieManager.clearNonConsentedStorage();
      }

      setIsInitialized(true);
    } catch (error) {
      console.error('Error initializing privacy state:', error);
      setIsInitialized(true);
    }
  };

  const updateConsent = (newConsent: ConsentData) => {
    setConsent(newConsent);
    setNeedsConsent(false);
    setShowConsentBanner(false);

    // Clean up storage based on new consent
    cookieManager.clearNonConsentedStorage();

    // Initialize privacy settings if not set
    if (!privacySettings) {
      const defaultSettings = privacyService.getPrivacySettings();
      setPrivacySettings(defaultSettings);
    }
  };

  const updatePrivacySettings = (newSettings: PrivacySettings) => {
    setPrivacySettings(newSettings);
    privacyService.setPrivacySettings(newSettings);
  };

  const clearAllData = () => {
    privacyService.clearAllData();
    setConsent(null);
    setPrivacySettings(null);
    setNeedsConsent(true);
    setShowConsentBanner(true);
  };

  const refreshConsent = () => {
    setNeedsConsent(true);
    setShowConsentBanner(true);
  };

  const contextValue: PrivacyContextType = {
    consent,
    privacySettings,
    needsConsent,
    canShowAds: privacyService.canShowAds(),
    canShowPersonalizedAds: privacyService.canShowPersonalizedAds(),
    canTrackAnalytics: privacyService.canTrackAnalytics(),
    canUseCookies: privacyService.canUseCookies(),
    updateConsent,
    updatePrivacySettings,
    clearAllData,
    refreshConsent
  };

  return (
    <PrivacyContext.Provider value={contextValue}>
      {children}
      {showConsentBanner && isInitialized && (
        <ConsentBanner
          onConsentGiven={updateConsent}
          onClose={() => setShowConsentBanner(false)}
        />
      )}
    </PrivacyContext.Provider>
  );
};

export const usePrivacy = (): PrivacyContextType => {
  const context = useContext(PrivacyContext);
  if (context === undefined) {
    throw new Error('usePrivacy must be used within a PrivacyProvider');
  }
  return context;
};

export default PrivacyContext;